type PreviewOwner = string | null | undefined;

interface PreviewEntry {
    url: string;
    owner: PreviewOwner;
}

interface OwnedDocumentPreviewTransactionOptions {
    isCurrent: () => boolean;
    initialOwner?: PreviewOwner;
    getFinalOwner: () => PreviewOwner;
    register: (owner: PreviewOwner, url: string) => unknown;
    release: (owner: PreviewOwner, url: string) => unknown;
}

/**
 * Owns Blob preview URLs across one asynchronous document-import transaction.
 * A stale Project releases every uncommitted URL exactly once. A successful
 * first save may adopt pending URLs under the new durable Project id, with the
 * new owner registered before the pending owner is released.
 */
export function createOwnedDocumentPreviewTransaction({
    isCurrent,
    initialOwner = null,
    getFinalOwner,
    register,
    release,
}: OwnedDocumentPreviewTransactionOptions) {
    let entries: PreviewEntry[] = [];
    let finished = false;

    const rollback = (): boolean => {
        if (finished) return false;
        finished = true;
        for (const entry of entries.splice(0)) release(entry.owner, entry.url);
        return true;
    };

    const guard = (): boolean => {
        if (!finished && isCurrent()) return true;
        rollback();
        return false;
    };

    const track = (url: unknown, owner: PreviewOwner = initialOwner): string | null => {
        if (finished || typeof url !== 'string' || !url.startsWith('blob:')) return null;
        entries.push({ url, owner });
        return url;
    };

    const commit = (referencedUrls: Iterable<unknown> = []): boolean => {
        if (!guard()) return false;
        const referenced = new Set(
            [...referencedUrls].filter((url): url is string => typeof url === 'string' && !!url),
        );
        const finalOwner = getFinalOwner();
        for (const entry of entries) {
            if (!referenced.has(entry.url)) {
                release(entry.owner, entry.url);
                continue;
            }
            if (String(entry.owner ?? '') !== String(finalOwner ?? '')) {
                register(finalOwner, entry.url);
                release(entry.owner, entry.url);
            }
        }
        entries = [];
        finished = true;
        return true;
    };

    return { track, guard, commit, rollback };
}
